import {
  type Allocation,
  type AllocationBE,
  AllocationValueTypeEnum,
  Field,
  FieldValidationLevel,
  type FieldValidationRule,
  type ISubaccount,
  NumericField,
  Unit,
} from '@talos/kyoko';
import Big from 'big.js';
import type { WritableDraft } from 'immer';

/** Defines the contents of a slice state that should exist for the utils in this file to be usable */
interface AllocationState {
  dependencies: { tradableSubAccounts: ISubaccount[] };
  form: {
    allocationValueTypeField: Field<AllocationValueTypeEnum>;
    allocations: { subAccountField: Field; valueField: NumericField }[];
    quantityField: NumericField;
  };
}

/** Validates your state.form.allocations (calls .validate on them) */
export function validateAllocations(state: WritableDraft<AllocationState>) {
  // If there are no sub accounts configured in the system, then allocations is not applicable
  // However it is a required field if it is configured
  const isSubAccountRequired = state.dependencies.tradableSubAccounts.length > 0;

  state.form.allocations = state.form.allocations.map(pair => ({
    subAccountField: pair.subAccountField.setIsRequired(isSubAccountRequired).validate(),
    valueField: pair.valueField.validate(isSubAccountRequired ? [allocationTotalValidation] : [], state.form),
  }));
}

/** Given a typical entity including either SubAccount or Allocation (or neither), initializes the state with this subacc info */
export function updateSubAccountOrAllocationState(
  state: WritableDraft<AllocationState>,
  { SubAccount, Allocation }: { SubAccount?: string; Allocation?: AllocationBE }
) {
  if (SubAccount != null) {
    const allocations: Allocation[] = [
      {
        subAccount: SubAccount,
        value: '1',
      },
    ];
    state.form.allocations = mapAllocationsToField(allocations, AllocationValueTypeEnum.Percentage);
  }

  if (Allocation) {
    const allocations: Allocation[] = Allocation.Allocations.map(alloc => ({
      subAccount: alloc.SubAccount,
      value: alloc.Value || '1',
    }));
    state.form.allocationValueTypeField = state.form.allocationValueTypeField.updateValue(Allocation.ValueType);
    state.form.allocations = mapAllocationsToField(allocations, Allocation.ValueType);
  }
}

/** Given an Allocation[] input, will return an array of allocation fields to use in your slice */
export function mapAllocationsToField(
  allocations: Allocation[],
  valueType: AllocationValueTypeEnum = AllocationValueTypeEnum.Percentage,
  isUserEntry = false
) {
  return allocations.map(alloc => ({
    // Allocation.subAccount sends empty string as empty selection, but we want that to be undefined here
    subAccountField: new Field({
      name: 'Sub Account',
      value: alloc.subAccount ? alloc.subAccount : undefined,
      isTouched: isUserEntry,
    }).validate(),
    valueField: new NumericField({
      name: 'Allocation amount',
      unit: valueType === AllocationValueTypeEnum.Percentage ? Unit.Percent : Unit.Decimal,
    }).updateValue(alloc.value, !isUserEntry),
  }));
}

/** Validates the total of your allocation fields, comparing it to your state's quantityField.value */
const allocationTotalValidation: FieldValidationRule<NumericField, WritableDraft<AllocationState['form']>> = (
  field,
  context
) => {
  const valueType = context?.allocationValueTypeField.value;
  const allocations = context?.allocations.map(pair => pair.valueField.bigValue) || [];
  const quantity = new Big(context?.quantityField.value || 0);

  const sum = allocations.reduce((acc: Big, value: Big | undefined) => {
    if (value) {
      return acc.add(value);
    }
    return acc;
  }, new Big(0));

  if (!field.hasValue || field.value === '0') {
    return {
      message: `${field.name} cannot be 0`,
      level: FieldValidationLevel.Error,
    };
  }

  if (valueType === AllocationValueTypeEnum.Percentage) {
    if (sum.gt(1)) {
      return {
        message: 'Total cannot exceed 100%',
        level: FieldValidationLevel.Error,
      };
    }
    if (sum.lt(1)) {
      return {
        message: 'Total must add up to 100%',
        level: FieldValidationLevel.Error,
      };
    }
  } else {
    if (sum.gt(quantity)) {
      return {
        message: 'Total cannot exceed Quantity',
        level: FieldValidationLevel.Error,
      };
    }
    if (sum.lt(quantity)) {
      return {
        message: 'Total must add up to Quantity',
        level: FieldValidationLevel.Error,
      };
    }
  }

  return null;
};

/** Disables allocationValueTypeField and the allocation array's internal fields */
export function disableAllocationFields(state: WritableDraft<AllocationState>, disable: boolean) {
  state.form.allocationValueTypeField = state.form.allocationValueTypeField.setDisabled(disable);
  state.form.allocations = state.form.allocations.map(pair => ({
    subAccountField: pair.subAccountField.setDisabled(disable),
    valueField: pair.valueField.setDisabled(disable),
  }));
}

/** Iterates over your state's allocation array and checks if any field has any error, or if you're missing an entry all-together */
export function allocationsAreValid(
  allocations: AllocationState['form']['allocations'],
  numTradableSubAccounts: number
) {
  const isSubAccountRequired = numTradableSubAccounts > 0;
  const allocationsHasError = isSubAccountRequired
    ? allocations.length === 0 ||
      allocations.flatMap(alloc => [alloc.subAccountField, alloc.valueField]).some(field => field.hasError)
    : false;
  return !allocationsHasError;
}

/** Sets the touched state of your state's allocation array */
export function touchAllocations(state: WritableDraft<AllocationState>, isTouched: boolean) {
  state.form.allocations = state.form.allocations.map(pair => ({
    subAccountField: pair.subAccountField.setTouched(isTouched),
    valueField: pair.valueField.setTouched(isTouched),
  }));
}
